<template>
  <div class="living-option-properties">
    <transition-group @before-enter="beforeEnter" @enter="enter" @afterEnter="afterEnter" @beforeLeave="beforeLeave" @leave="leave" @afterLeave="afterLeave">
      <router-link v-for="(child, index) in activeProperties" :key="child._id" :data-index="index" tag="div" :to="getRouterLink(child)">
        <AvailabilityMarker v-if="child.marker" :filtered="child.filtered" :x="child.marker.x" :y="child.marker.y" :status="child.status" :isDisplayHome="child.isDisplayHome" :productType="child.productType" :specialCondition="child.specialCondition" :hide="child.hide" />

        <polygon-shape v-for="(polygon, i) in child.polygons" :key="polygon._id || `${child._id}-${i}`" :polygon="polygon" />
      </router-link>
    </transition-group>
  </div>
</template>

<script>
import AvailabilityMarker from "@/components/AvailabilityMarker";
import PolygonShape from "@/components/PolygonShape";
import { mapState } from "vuex";
import { bus } from "../main";
import _ from "lodash";
import { buildProductDisplayHomesPath, buildPropertyPath, getLivingPathway } from "@/utils/livingRoute";

export default {
  props: ["properties"],

  components: { AvailabilityMarker, PolygonShape },

  data() {
    return {
      activeProperties: [],
    };
  },

  watch: {
    properties: {
      handler(newProperties, oldProperties) {
        if (!_.isEqual(oldProperties, newProperties)) {
          if (oldProperties && oldProperties.length) {
            this.activeProperties = [];
          }

          this.$store.commit("addAnimationToQueue", "LivingOptionProperties");
        }
      },
      immediate: true,
    },
  },

  computed: {
    ...mapState({
      inputBaths: (state) => state.filter.inputBaths,
      inputBeds: (state) => state.filter.inputBeds,
      inputCars: (state) => state.filter.inputCars,
      inputPriceRange: (state) => state.filter.inputPriceRange,
      inputDwellingPriceRange: (state) => state.filter.inputDwellingPriceRange,
      inputLotSizeRange: (state) => state.filter.inputLotSizeRange,
      inputDwellingSizeRange: (state) => state.filter.inputDwellingSizeRange,
      inputLotNumber: (state) => state.filter.inputLotNumber,
      inputBuilder: (state) => state.filter.inputBuilder,
      inputColourScheme: (state) => state.filter.inputColourScheme,
      inputHouseDesign: (state) => state.filter.inputHouseDesign,
    }),

    activePropertyId() {
      const propertyId = this.$route.params.propertyId;
      return propertyId ? propertyId : null;
    },

    activeSubsectionId() {
      return this.$store.state.route.query.activeSubsectionId;
    },

    config() {
      return this.$store.state.newConfig;
    },
  },

  mounted() {
    bus.$on("animateLivingOptionProperties", () => {
      if (!_.isEqual(this.activeProperties, this.properties)) {
        this.activeProperties = this.properties;
      } else {
        bus.$emit("finishedAnimation", "LivingOptionProperties");
      }
    });
  },

  methods: {
    getRouterLink(child) {
      // Interaction Guard based on Status, return current URL if we're not meant to interact with the property
      if ((child.status === "Sold" && !this.config.general.canInteractSoldProperties) || (child.status === "On Hold" && !this.config.general.canInteractOnHoldProperties) || (child.status === "Not Released" && !this.config.general.canInteractNotReleasedProperties)) {
        return this.$route.fullPath;
      }

      if (child.to) {
        return child.to;
      } else {
        if (child.isDisplayHome) {
          return buildProductDisplayHomesPath(this.$route.params.categoryId, child._id);
        } else {
          return buildPropertyPath(this.$route, this.$route.params.livingOptionId, this.$route.params.subsectionId, child._id, `?activeSubsectionId=${this.activeSubsectionId}`);
        }
      }
    },

    polygonUrl(id) {
      return `/category/${this.$route.params.categoryId}/property/${id}?activeSubsectionId=${this.activeSubsectionId}`;
    },

    isFiltered(property) {
      const pathway = getLivingPathway(this.$store.state.route);

      let builder = "";
      if (pathway !== "land" && property.dwelling && property.dwelling.builderLabel) {
        builder = property.dwelling.builderLabel;
      }

      let colourScheme = "";
      if (pathway !== "land" && property.dwelling && property.dwelling.colourScheme) {
        colourScheme = property.dwelling.colourScheme;
      }

      let houseDesign = "";
      if (pathway !== "land" && property.dwelling && property.dwelling.houseDesign) {
        houseDesign = property.dwelling.houseDesign;
      }

      const bath = property.dwelling ? parseInt(property.dwelling.baths) : null;
      const bed = property.dwelling ? parseInt(property.dwelling.beds) : null;
      const car = property.dwelling ? parseInt(property.dwelling.cars) : null;

      const price = pathway === "land" ? property.price / 1000 : property.dwelling && property.dwelling.price ? property.dwelling.price / 1000 : 0;
      const size = property.size;
      const sizeDwelling = property.dwelling ? property.dwelling.size : null;
      const lot = property.label.toLowerCase().replace("lot ", "");

      const inputBaths = this.inputBaths;
      const inputBeds = this.inputBeds;
      const inputCars = this.inputCars;
      const inputPriceRange = this.inputPriceRange;
      const inputLotSizeRange = this.inputLotSizeRange;
      const inputDwellingSizeRange = this.inputDwellingSizeRange;
      const inputLotNumber = this.inputLotNumber;
      const inputBuilder = this.inputBuilder;
      const inputColourScheme = this.inputColourScheme;
      const inputHouseDesign = this.inputHouseDesign;

      let isFiltered = false;

      if (inputBuilder && builder !== inputBuilder) {
        isFiltered = true;
      }

      if (inputColourScheme && colourScheme !== inputColourScheme) {
        isFiltered = true;
      }

      if (inputHouseDesign && houseDesign !== inputHouseDesign) {
        isFiltered = true;
      }

      if (inputBaths && bath !== inputBaths) {
        isFiltered = true;
      }

      if (inputBeds && bed !== inputBeds) {
        isFiltered = true;
      }

      if (inputCars && car !== inputCars) {
        isFiltered = true;
      }

      if (inputLotNumber && lot !== inputLotNumber) {
        isFiltered = true;
      }

      if (!(price >= inputPriceRange[0] && price <= inputPriceRange[1])) {
        isFiltered = true;
      }

      if (!(size >= inputLotSizeRange[0] && size <= inputLotSizeRange[1])) {
        isFiltered = true;
      }

      if (pathway !== "land") {
        if (sizeDwelling && !(sizeDwelling >= inputDwellingSizeRange[0] && sizeDwelling <= inputDwellingSizeRange[1])) {
          isFiltered = true;
        }
      }

      const ignorePropertyStatuses = this.config.general && this.config.general.ignorePropertyStatusFiltering;

      if (ignorePropertyStatuses.includes(property.status)) {
        isFiltered = false;
      }

      if (property.isDisplayHome) {
        isFiltered = false;
      }

      return isFiltered;
    },

    beforeEnter(el) {
      el.removeAttribute("style");
      el.style.opacity = 0;
    },

    enter(el, done) {
      setTimeout(() => {
        el.style.opacity = 1;
        el.style.transition = `opacity 1.5s`;
        setTimeout(() => {
          done();
        }, 300);
      }, (parseInt(el.dataset.index) + 1) * 20);
    },

    afterEnter(el) {
      if (parseInt(el.dataset.index) + 1 === this.activeProperties.length) {
        bus.$emit("finishedAnimation", "LivingOptionProperties");
      }
      el.removeAttribute("style");
    },

    beforeLeave(el) {
      el.style.transition = `transform 0.3s cubic-bezier(0.32, 0, 0.67, 0), opacity 0.3s`;
    },

    leave(el) {
      el.style.opacity = 0;
      el.style.transform = "translateY(25%)";
    },

    afterLeave(el) {
      el.removeAttribute("style");
    },
  },
};
</script>

<style lang="scss" scoped>
.living-option-properties {
  position: absolute;
  top: 0;
  z-index: var(--tag-z-index);
}
</style>
